/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import type { Dayjs } from 'dayjs';
import {
  concat,
  entries,
  every,
  filter,
  find,
  findIndex,
  findKey,
  first,
  flatMap,
  flow,
  get,
  has,
  includes,
  intersection,
  isArray,
  isEmpty,
  map,
  omit,
  parseInt,
  partialRight,
  reduce,
  size,
  some,
  split,
  union,
  unionBy,
  values,
} from 'lodash';

import {
  gridClasses,
  GridColDef,
  type GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro';

import { SearchDescription, SearchDescriptionAdvanced } from '@libraries/api/gen-api';

import type {
  AdvancedSearchActions,
  AdvancedSearchState,
  BooleanFilterState,
  DateFilterState,
  FilterState,
  FilterStates,
  FilterStateUpdate,
  GridColumnWidthModel,
  IntFilterState,
  SearchTypeSpecificProps,
  StringFilterState,
  StringSelectFilterState,
} from './types';

import type {
  AdvancedSearchRuleDefinitions,
  AdvancedSearchRules,
  AdvancedSearchStringOperation,
} from '../../../../types/types';

import { isNotEmpty, toDayjs } from '../../../utils';

/**
 * Resets the time of the `to` date of the date filter to 23:59:59 if the date changed and the time has not
 */
export function reconcileToDate(prevDate: Dayjs, nextDate: Dayjs) {
  if (nextDate === null) {
    return null;
  }

  const setToEOD =
    // Previous date is not present and time is set to the default value -> set time to EOD
    (prevDate === null && nextDate.hour() === 0 && nextDate.minute() === 0) ||
    // Previous date is present, date has changed, but time has not -> set time to EOD
    (prevDate !== null &&
      !nextDate.isSame(prevDate, 'date') &&
      (nextDate.hour() === prevDate.hour() || nextDate.minute() === prevDate.minute()));

  return setToEOD ? toDayjs(nextDate).endOf('day') : toDayjs(nextDate);
}

/**
 * Returns the 'like' operation otherwise defaults to the first supported operation
 */
export const getDefaultStringOperation = (operations: Array<AdvancedSearchStringOperation>) => {
  if (includes(operations, 'like')) {
    return 'like';
  }

  return first(operations);
};

/**
 * Maps a rule to advanced search state
 */
export const mapFilterRuleToState = (
  field: string,
  rule: AdvancedSearchRuleDefinitions,
  initialstate: FilterStates
) => {
  const baseState = {
    field,
    type: rule.type,
    labelKey: rule.labelKey,
    readOnly: rule.readonly,
  };

  switch (rule.type) {
    case 'date':
      return {
        ...baseState,
        value: get(initialstate, `${field}.value`, { from: null, to: null }),
      };
    case 'string':
      return {
        ...baseState,
        operations: rule.operations,
        operation: get(initialstate, `${field}.operation`, getDefaultStringOperation(rule.operations)),
        value: get(initialstate, `${field}.value`, ''),
      };
    case 'string-select':
      return {
        ...baseState,
        options: rule.options,
        value: get(initialstate, `${field}.value`, []),
      };
    case 'boolean':
      return {
        ...baseState,
        value: get(initialstate, `${field}.value`, ''),
      };
    case 'int':
      return {
        ...baseState,
        options: [...rule.options],
        value: map(get(initialstate, `${field}.value`, []), parseInt),
      };
    default:
      throw new Error(`Type not supported`);
  }
};

/** Converts string to BooleanFilterState value */
export const parseBooleanString = (value?: string): BooleanFilterState['value'] => {
  if (includes(['true', 'false'], value)) {
    return value as BooleanFilterState['value'];
  }

  return '';
};

/**
 * Creates search state from an array of query strings
 */
export const parseQueryStrings = (
  advancedSearchRules: AdvancedSearchRules,
  queryStrings: Array<string>
): FilterStates =>
  reduce(
    queryStrings,
    (state, queryString) => {
      const parsed = split(queryString, '::');
      const [field, operator, query] = parsed;

      switch (operator) {
        case 'range': {
          const [from, to] = split(query, '|');

          return {
            ...state,
            [field]: {
              value: {
                from: isEmpty(from) ? null : toDayjs(from),
                to: isEmpty(to) ? null : toDayjs(to),
              },
            },
          };
        }
        case 'like': {
          return {
            ...state,
            [field]: {
              value: query,
              operation: operator,
            },
          };
        }
        case 'exact': {
          const type = get(advancedSearchRules, [field, 'type']);

          if (type === 'string-select') {
            if (has(state, field)) {
              return {
                ...state,
                [field]: {
                  value: [...get(state, [field, 'value']), query],
                },
              };
            }

            return {
              ...state,
              [field]: {
                value: [query],
              },
            };
          }

          return {
            ...state,
            [field]: {
              value: query,
              operation: operator,
            },
          };
        }
        case '=': {
          const type = get(advancedSearchRules, [field, 'type']);

          switch (type) {
            case 'boolean': {
              return {
                ...state,
                [field]: {
                  value: parseBooleanString(query),
                },
              };
            }
            case 'int': {
              const value = parseInt(query);

              if (has(state, field)) {
                return {
                  ...state,
                  [field]: {
                    value: [...get(state, [field, 'value']), value],
                  },
                };
              }

              return {
                ...state,
                [field]: {
                  value: [value],
                },
              };
            }
            default:
              throw new Error(`Unhandled rule ${type} with operator type =`);
          }
        }
        default:
          throw new Error(`Operator ${operator} not supported`);
      }
    },
    {}
  );

export function getDefaultFilterValue<TFilterState extends FilterState>(
  type: TFilterState['type']
): TFilterState['value'] {
  switch (type) {
    case 'date':
      return {
        from: null,
        to: null,
      };
    case 'string':
    case 'boolean':
      return '';
    case 'string-select':
      return [] as Array<string>;
    case 'int':
      return [] as Array<number>;
    default:
      throw new Error('Type not supported');
  }
}

export const defaultProductFilters = ['is_virtual_product::=::false', 'is_subscription_product::=::false'];

export const getProductFilter = (search: string | string[]) => {
  if (isEmpty(search)) {
    return defaultProductFilters;
  }
  if (isArray(search)) {
    return [...defaultProductFilters, ...search];
  }
  return [...defaultProductFilters, `product_name::like::${search}`];
};

export const resetFilter = (filter: FilterState) => {
  if (filter.readOnly) {
    return { ...filter };
  }
  switch (filter.type) {
    case 'string':
      return {
        ...filter,
        value: getDefaultFilterValue(filter.type),
        operation: getDefaultStringOperation(filter.operations),
      };
    case 'date':
    case 'string-select':
    case 'boolean':
    case 'int':
      return {
        ...filter,
        value: getDefaultFilterValue(filter.type),
      };
    default:
      throw new Error('Type not supported');
  }
};

export const initializeFilters = (
  advancedSearchRules: AdvancedSearchRules,
  initialAdvancedSearchQuery?: string[]
): FilterStates => {
  const initialState = parseQueryStrings(advancedSearchRules, initialAdvancedSearchQuery);

  return reduce(
    entries(advancedSearchRules),
    (acc, [field, rule]) => ({
      ...acc,
      [field]: {
        ...mapFilterRuleToState(field, rule, initialState),
      },
    }),
    {}
  );
};

export function isFilterStateEqualToUpdate(filterState: FilterState, update: FilterStateUpdate[string]) {
  switch (filterState.type) {
    case 'date': {
      const from = get(filterState, ['value', 'from']);
      const fromUpdate = get(update, ['value', 'from']);
      const isFromEqualToUpdate =
        from === fromUpdate || (isNotEmpty(from) && isNotEmpty(fromUpdate) && from.isSame(fromUpdate));

      const to = get(filterState, ['value', 'to']);
      const toUpdate = get(update, ['value', 'to']);
      const isToEqualToUpdate =
        to === toUpdate || (isNotEmpty(to) && isNotEmpty(toUpdate) && to.isSame(toUpdate));

      return isFromEqualToUpdate && isToEqualToUpdate;
    }
    case 'int':
    case 'string-select':
      return (
        size(update.value) === size(filterState.value) &&
        size(intersection(filterState.value as any, update.value as any)) === size(update.value)
      );
    case 'string':
      return (
        filterState.value === (update.value as any) && filterState.operation === (update.operation as any)
      );
    case 'boolean':
      return filterState.value === (update.value as any);
    default:
      throw new Error('Unhandled filterState type');
  }
}

export function reconcileQuickFiltersWithNewFilterState(
  quickFilters: AdvancedSearchState['quickFilters'],
  field: string,
  filterState: FilterState
): AdvancedSearchState['quickFilters'] {
  const activeQuickFilterKey = findKey(quickFilters, (quickFilter) => quickFilter.active);

  // quickFilter still applies if it's active and the quickFilter's value matches the new filterState's value (or the quickFilter does not apply to this filterState)
  const doesQuickFilterStillApply =
    isNotEmpty(activeQuickFilterKey) &&
    (!has(quickFilters, [activeQuickFilterKey, 'updates', field]) ||
      isFilterStateEqualToUpdate(filterState, get(quickFilters, [activeQuickFilterKey, 'updates', field])));

  return reduce(
    quickFilters,
    (quickFilters, quickFilter, quickFilterKey) => {
      return {
        ...quickFilters,
        [quickFilterKey]: {
          ...quickFilter,
          active: doesQuickFilterStillApply && activeQuickFilterKey === quickFilterKey,
        },
      };
    },
    {}
  );
}

export const advancedSearchReducer = (
  state: AdvancedSearchState,
  action: AdvancedSearchActions
): AdvancedSearchState => {
  const { type } = action;

  switch (type) {
    case 'set_date_value': {
      const { payload } = action;
      const [from, to] = payload.value;
      const filter = state.filterStates[payload.field] as DateFilterState;

      const newFilterState: DateFilterState = {
        ...filter,
        value: {
          from,
          to: reconcileToDate(filter.value.to, to),
        },
      };

      return {
        ...state,
        quickFilters: reconcileQuickFiltersWithNewFilterState(
          get(state, 'quickFilters'),
          payload.field,
          newFilterState
        ),
        filterStates: {
          ...state.filterStates,
          [payload.field]: newFilterState,
        },
      };
    }
    case 'set_string_value': {
      const {
        payload: { field, value },
      } = action;

      const newFilterState: StringFilterState = {
        ...(state.filterStates[field] as StringFilterState),
        value,
      };

      return {
        ...state,
        quickFilters: reconcileQuickFiltersWithNewFilterState(
          get(state, 'quickFilters'),
          field,
          newFilterState
        ),
        filterStates: {
          ...state.filterStates,
          [field]: newFilterState,
        },
      };
    }
    case 'set_string_operation': {
      const { payload } = action;

      const filter = state.filterStates[payload.field] as StringFilterState;

      const newFilterState: StringFilterState = {
        ...filter,
        operation: payload.value as AdvancedSearchStringOperation,
      };

      return {
        ...state,
        quickFilters: reconcileQuickFiltersWithNewFilterState(
          get(state, 'quickFilters'),
          payload.field,
          newFilterState
        ),
        filterStates: {
          ...state.filterStates,
          [payload.field]: newFilterState,
        },
      };
    }
    case 'set_string_select_value': {
      const { payload } = action;

      const filter = state.filterStates[payload.field] as StringSelectFilterState;

      const newFilterState: StringSelectFilterState = {
        ...filter,
        value: payload.value,
      };

      return {
        ...state,
        quickFilters: reconcileQuickFiltersWithNewFilterState(
          get(state, 'quickFilters'),
          payload.field,
          newFilterState
        ),
        filterStates: {
          ...state.filterStates,
          [payload.field]: newFilterState,
        },
      };
    }
    case 'set_boolean_value': {
      const { payload } = action;

      const filter = state.filterStates[payload.field] as BooleanFilterState;

      const newFilterState: BooleanFilterState = {
        ...filter,
        value: payload.value,
      };

      return {
        ...state,
        quickFilters: reconcileQuickFiltersWithNewFilterState(
          get(state, 'quickFilters'),
          payload.field,
          newFilterState
        ),
        filterStates: {
          ...state.filterStates,
          [payload.field]: newFilterState,
        },
      };
    }
    case 'set_int_value': {
      const { payload } = action;

      const filter = state.filterStates[payload.field] as IntFilterState;

      const newFilterState = {
        ...filter,
        value: payload.value,
      };

      return {
        ...state,
        quickFilters: reconcileQuickFiltersWithNewFilterState(
          get(state, 'quickFilters'),
          payload.field,
          newFilterState
        ),
        filterStates: {
          ...state.filterStates,
          [payload.field]: newFilterState,
        },
      };
    }
    case 'set_status': {
      return {
        ...state,
        status: action.payload,
      };
    }
    case 'apply_quick_filter': {
      const { payload } = action;

      // Find quick filter so we can apply it to filter
      const activeQuickFilter = find(state.quickFilters, (_, key) => key === payload);

      return {
        status: 'uncommitted',
        // Mark the active quick filter as such and all other quick filters as false
        quickFilters: reduce(
          state.quickFilters,
          (quickFilters, quickFilter, quickFilterKey) => ({
            ...quickFilters,
            [quickFilterKey]: {
              updates: {
                ...quickFilter.updates,
              },
              active: payload === quickFilterKey,
            },
          }),
          {}
        ),
        filterStates: {
          ...reduce(
            state.filterStates,
            (newFilterStates, filterState, filterStateKey) => {
              // If the quick filter has an update for this field, apply it
              if (has(activeQuickFilter, ['updates', filterStateKey])) {
                return {
                  ...newFilterStates,
                  [filterStateKey]: {
                    ...filterState,
                    value: get(activeQuickFilter, ['updates', filterStateKey, 'value']),
                    ...(filterState.type === 'string'
                      ? { operation: get(activeQuickFilter, ['updates', filterStateKey, 'operation']) }
                      : {}),
                  },
                };
              }

              return {
                ...newFilterStates,
                [filterStateKey]: {
                  ...filterState,
                },
              };
            },
            {}
          ),
        },
      };
    }
    case 'reset_quick_filter': {
      const activeQuickFilter = find(get(state, 'quickFilters'), (quickFilter) => quickFilter.active);

      if (isEmpty(activeQuickFilter)) {
        return { ...state };
      }

      return {
        ...state,
        quickFilters: reduce(
          state.quickFilters,
          (quickFilters, quickFilter, field) => ({
            ...quickFilters,
            [field]: {
              updates: {
                ...quickFilter.updates,
              },
              active: false,
            },
          }),
          {}
        ),
        filterStates: reduce(
          state.filterStates,
          (filterStates, filterState, field) => ({
            ...filterStates,
            [field]: has(activeQuickFilter, ['updates', field])
              ? resetFilter(filterState)
              : {
                  ...filterState,
                },
          }),
          {}
        ),
      };
    }
    case 'initialize_filters': {
      const {
        payload: { advancedSearchRules, initialAdvancedSearchQuery, quickFilters },
      } = action;

      const newFilterStates = initializeFilters(advancedSearchRules, initialAdvancedSearchQuery);

      const prevActiveQuickFilterKey = findKey(state.quickFilters, (quickFilter) => quickFilter.active);
      const quickFilter = get(quickFilters, [prevActiveQuickFilterKey]);

      const doesQuickFilterStillApply =
        isNotEmpty(prevActiveQuickFilterKey) &&
        isNotEmpty(quickFilter) &&
        every(
          quickFilter.getFilterStateUpdates(),
          (update, field) =>
            has(newFilterStates, field) && isFilterStateEqualToUpdate(get(newFilterStates, field), update)
        );

      return {
        ...state,
        status: 'uncommitted',
        quickFilters: reduce(
          quickFilters,
          (quickFilters, quickFilter, field) => {
            return {
              ...quickFilters,
              [field]: {
                active: field === prevActiveQuickFilterKey && doesQuickFilterStillApply,
                updates: quickFilter.getFilterStateUpdates(),
              },
            };
          },
          {}
        ),
        filterStates: newFilterStates,
      };
    }
    case 'reset_filters': {
      return {
        ...state,
        status: 'committed',
        quickFilters: reduce(
          state.quickFilters,
          (quickFilters, quickFilter, quickFilterKey) => ({
            ...quickFilters,
            [quickFilterKey]: {
              ...quickFilter,
              active: false,
            },
          }),
          {}
        ),
        filterStates: reduce(
          entries(state.filterStates),
          (acc, [field, filter]) => ({
            ...acc,
            [field]: {
              ...resetFilter(filter),
            },
          }),
          {}
        ),
      };
    }
    default:
      throw new Error(`Action ${type} not supported`);
  }
};

/** Converts boolean string value to boolean */
export const booleanStringToBoolean = (value: BooleanFilterState['value']): boolean =>
  isEmpty(value) ? undefined : value === 'true';

/**
 * Safely converts a `Dayjs` value to an ISO-formatted string or an empty string if undefined
 */
export const safeToISOString = (value?: Dayjs) => (value?.isValid() ? value.toISOString() : '');

/**
 * Builds an advanced search query (array of strings) from the advanced search state
 */
export const buildQueryString = (filterStates: FilterStates) =>
  flatMap(
    filter(values(filterStates), (filterState) => {
      const { type, value } = filterState;
      switch (type) {
        case 'date': {
          const typedValue = value;
          return (
            (isNotEmpty(typedValue?.from) && typedValue.from.isValid()) ||
            (isNotEmpty(typedValue?.to) && typedValue.to.isValid())
          );
        }
        case 'string': {
          return isNotEmpty(value?.trim());
        }
        case 'string-select':
        case 'boolean':
        case 'int': {
          return isNotEmpty(value);
        }
        default:
          throw new Error('Type not supported');
      }
    }),
    (filterState): string | Array<string> => {
      const { type, field, value } = filterState;
      switch (type) {
        case 'date': {
          return [`${field}::range::${safeToISOString(value?.from)}|${safeToISOString(value?.to)}`];
        }
        case 'string': {
          return [`${field}::${filterState.operation}::${value.trim()}`];
        }
        case 'string-select': {
          return map(value, (str) => `${field}::exact::${str}`);
        }
        case 'boolean': {
          return [`${field}::=::${booleanStringToBoolean(value)}`];
        }
        case 'int': {
          return map(value, (int) => `${field}::=::${int}`);
        }
        default:
          throw new Error('Type not supported');
      }
    }
  );

/**
 * Gets the count of active filters for displaying on the 'Filters' button
 */
export const getActiveFilterCount = (filterStates: FilterStates) =>
  reduce(
    values(filterStates),
    (count, { type, value }) => {
      switch (type) {
        case 'date':
          return value?.from?.isValid() || value?.to?.isValid() ? count + 1 : count;
        case 'string':
          return isNotEmpty(value?.trim()) ? count + 1 : count;
        case 'string-select':
        case 'boolean':
        case 'int':
          return isNotEmpty(value) ? count + 1 : count;
        default:
          throw new Error('Unsupported type');
      }
    },
    0
  );

/** A map of advanced search description rules by their field name */
export const getSearchDescriptionMap = (
  searchDescription?: SearchDescription
): Record<string, SearchDescriptionAdvanced> =>
  reduce(searchDescription?.advanced, (acc, field) => ({ ...acc, [field.name]: { ...field } }), {});

/**
 * Creates a Set of supported fields to sort on based on the search description rules.
 */
export const getSortableFields = (searchDescription?: SearchDescription): Set<string> =>
  new Set(concat(get(searchDescription, 'basic', []), map(get(searchDescription, 'advanced', []), 'name')));

/**
 * Filters out advanced search rules, operations, and values
 * that aren't supported by the backend. Does not include rules supported by the backend
 * that aren't also supported by the frontend.
 * @param advancedSearchRules rules supported by the frontend
 * @param searchDescription rules supported by the backend
 * @returns reconciled rules
 */
export const reconcileAdvancedSearchRules = (
  advancedSearchRules: AdvancedSearchRules,
  searchDescription: SearchDescription
): AdvancedSearchRules => {
  const searchDescriptionMap = getSearchDescriptionMap(searchDescription);

  return reduce(
    entries(advancedSearchRules),
    (reconciledRules, [field, definition]) => {
      switch (definition.type) {
        case 'string': {
          if (has(searchDescriptionMap, field)) {
            return {
              ...reconciledRules,
              [field]: {
                ...definition,
                operations: intersection(definition.operations, searchDescriptionMap[field].operations),
              },
            };
          }
          if (
            isNotEmpty(definition.fields) &&
            every(definition.fields, (subField) => has(searchDescriptionMap, subField))
          ) {
            return {
              ...reconciledRules,
              [field]: {
                ...definition,
                operations: intersection(
                  definition.operations,
                  flatMap(
                    map(definition.fields, (subField) =>
                      get(searchDescriptionMap, `${subField}.operations`, [])
                    )
                  )
                ),
              },
            };
          }

          return reconciledRules;
        }
        case 'string-select': {
          if (has(searchDescriptionMap, field)) {
            return {
              ...reconciledRules,
              [field]: {
                ...definition,
                operations: intersection(definition.operations, searchDescriptionMap[field].operations),
                options: intersection(definition.options, searchDescriptionMap[field].select_values),
              },
            };
          }

          return reconciledRules;
        }
        case 'date': {
          if (has(searchDescriptionMap, field)) {
            return {
              ...reconciledRules,
              [field]: {
                ...definition,
                operations: intersection(definition.operations, searchDescriptionMap[field].operations),
              },
            };
          }

          return reconciledRules;
        }
        case 'boolean': {
          if (has(searchDescriptionMap, field)) {
            return {
              ...reconciledRules,
              [field]: {
                ...definition,
                operations: intersection(definition.operations, searchDescriptionMap[field].operations),
              },
            };
          }

          return reconciledRules;
        }
        case 'int': {
          if (has(searchDescriptionMap, field)) {
            return {
              ...reconciledRules,
              [field]: {
                ...definition,
                operations: intersection(definition.operations, searchDescriptionMap[field].operations),
              },
            };
          }

          return reconciledRules;
        }
        default:
          throw new Error('Unsupported search type');
      }
    },
    {}
  );
};

export function areFilterStatesEmpty(filterStates: FilterStates) {
  const isFilterStateEmpty = (filterState: FilterState) => {
    switch (filterState.type) {
      case 'date':
        return isEmpty(get(filterState, ['value', 'from'])) && isEmpty(get(filterState, ['value', 'to']));
      case 'boolean':
      case 'int':
      case 'string':
      case 'string-select':
        return isEmpty(filterState.value);
      default:
        throw new Error('Unhandled FilterState type');
    }
  };

  return every(values(filterStates), isFilterStateEmpty);
}

/** Sets the `filterable` prop for columns based on the provided the advancedSearchRules */
export const reconcileFilterableColumn = (
  column: GridColDef,
  advancedSearchRules: AdvancedSearchRules
): GridColDef => ({
  ...column,
  filterable: has(advancedSearchRules, column.field),
});

/** Sets the `width` prop for columns based on the provided columnWidthOverrides.
 *  Removes the `flex` prop if a width override exists.
 */
export const reconcileColumnWidth = (
  column: GridColDef,
  columnWidthOverrides?: GridColumnWidthModel
): GridColDef => ({
  ...column,
  width: get(columnWidthOverrides, column.field, column.width),
  flex: has(columnWidthOverrides, column.field) ? undefined : column.flex,
});

/** Sets the `sortable` prop for columns based on the provided sortableColumns Set */
export const reconcileSortableColumn = (column: GridColDef, sortableFields: Set<string>): GridColDef => ({
  ...column,
  sortable: column.sortable ? sortableFields.has(column.field) : false,
});

/** Sets the order of columns based on the provided columnOrderModel */
export const reconcileColumnOrder = (
  columns: Readonly<Array<GridColDef>>,
  columnOrderModel: Array<GridColDef['field']>
): Readonly<Array<GridColDef>> => {
  return unionBy(
    reduce(
      columnOrderModel,
      (reconciledColumns, columnField) => {
        const column = find(columns, ['field', columnField]);

        if (column) {
          return [...reconciledColumns, column];
        }

        return reconciledColumns;
      },
      []
    ),
    columns,
    'field'
  );
};

/**
 * Given a column order model and a list of columns:
 *  - removes fields from the column order model that do not exist in the list of columns
 *  - appends fields from the list of columns that do not exist in the column order model
 *
 * ```ts
 *  let columnOrderModel = ['column_two', 'column_one']
 *  let columns = [{ field: 'column_one' }, { field: 'column_three' }]
 *
 *  reconcileColumnOrderModel(columnOrderModel, columns)
 *
 *  // Produces: ['column_one', 'column_three']
 * ```
 */
export const reconcileColumnOrderModel = (
  columnOrderModel: Array<string>,
  columns: Readonly<Array<GridColDef>>
): Array<string> => {
  const columnFields = map(columns, 'field');

  return union(intersection(columnOrderModel, columnFields), columnFields);
};

/**
 * Given the column that was moved, the current column order, and the list of columns - moves a column to a new location and adjusts its neighbor accordingly
 *
 * **NOTE: Some special MUI columns (row selection, row expansion) aren't listed in the `columns` definition provided to the `SearchTable`, and MUI
 * counts those special columns as part of the list when the column order changes. To account for that, we have to find the true index of the column that was moved
 * and then calculate an offset relative to what MUI specified as the `oldIndex` to know the true `targetIndex`.**
 */
export const updateColumnOrderModel = (
  { oldIndex, targetIndex, column }: GridColumnOrderChangeParams,
  columnOrderModel: Array<string>,
  columns: Array<GridColDef>
): Array<string> => {
  const trueOldIndex = findIndex(columns, ['field', column.field]);
  const offset = trueOldIndex - oldIndex;
  const trueTargetIndex = targetIndex + offset;

  const fromField = column.field;
  const toField = get(columns, [trueTargetIndex, 'field']);
  const pushRight = trueOldIndex > trueTargetIndex;

  return reduce(
    columnOrderModel,
    (newColumnOrderModel, currentField) => {
      switch (true) {
        case currentField === fromField && toField !== fromField:
          return newColumnOrderModel;
        case currentField === fromField && toField === fromField:
          return [...newColumnOrderModel, currentField];
        case currentField === toField && pushRight:
          return [...newColumnOrderModel, fromField, currentField];
        case currentField === toField && !pushRight:
          return [...newColumnOrderModel, currentField, fromField];
        default:
          return [...newColumnOrderModel, currentField];
      }
    },
    [] as Array<string>
  );
};

/**
 * Reconciles the `width`, `sortable`, `flex`, and `filterable` properties
 * of the provided `columns` definition
 * */
export const reconcileColumns = (
  columns: Readonly<Array<GridColDef>>,
  advancedSearchRules: AdvancedSearchRules,
  sortableFields: Set<string>,
  columnWidthOverrides?: GridColumnWidthModel,
  columnOrderModel?: Array<GridColDef['field']>
): Array<GridColDef> => {
  return map(
    reconcileColumnOrder(columns, columnOrderModel),
    flow(
      partialRight(reconcileFilterableColumn, advancedSearchRules),
      partialRight(reconcileColumnWidth, columnWidthOverrides),
      partialRight(reconcileSortableColumn, sortableFields)
    )
  );
};

/**
 * Reconciles the `width` and `flex` properties
 * of the provided `columns` definition for a ClientSearchTable
 * */
export const reconcileColumnsForClientSearch = (
  columns: Readonly<Array<GridColDef>>,
  columnWidthOverrides: GridColumnWidthModel
): Array<GridColDef> => map(columns, (column) => reconcileColumnWidth(column, columnWidthOverrides));

/** A column visibility model that includes what the user has hidden
 *  and what is hidden by the system */
export const getOverriddenColumnVisibilityModel = (
  hiddenColumns: Array<string>,
  columnVisibilityModel: GridColumnVisibilityModel
) =>
  reduce(
    hiddenColumns,
    (acc, column) => ({
      ...acc,
      [column]: false,
    }),
    columnVisibilityModel ?? {}
  );

/**
 * Indicates if the user has hidden any columns that are **visible** to them,
 * and not hidden at the system-level.
 * @param columns a list of columns provided to data grid
 * @param hiddenColumns a list of columns that should be hidden from the user
 * @param columnVisibilityModel column visibility model provided to data grid
 * @returns a boolean indicating if the user has hidden any columns
 */
export const hasHiddenColumns = (
  columns: Array<GridColDef>,
  hiddenColumns: Array<string>,
  columnVisibilityModel: GridColumnVisibilityModel
) =>
  some(
    entries(columnVisibilityModel),
    ([key, value]) =>
      !value && !includes(hiddenColumns, key) && some(columns, (column) => column.field === key)
  );

/**
 *
 * @param columns a list of columns provided to data grid
 * @param hiddenColumns a list of system-defined columns that cannot be made visible
 * @returns a list of columns that can be made visible or hidden by the user
 */
export const mapTogglableColumns = (columns: Array<GridColDef>, hiddenColumns: Array<string>) =>
  map(
    filter(columns, ({ field }) => !includes(hiddenColumns, field)),
    'field'
  );

/**
 * Helper function for destructuring searchType-specific props
 * @param props searchType-specific props
 * @returns searchType-specific props with possible props made optional
 */
export const getSearchTypeSpecificProps = (
  props: SearchTypeSpecificProps
): {
  getSearchDescription?: () => Promise<void>;
  advancedSearchRules?: AdvancedSearchRules;
} => ({ ...omit(props, 'searchType') });

/** Styles for hiding cell outlines when cells are focused */
export const headerDisabledClass = {
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
    outline: 'none',
  },
};

/** Styles for hiding cell outlines when cells are focused */
export const cellDisabledClass = {
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: 'none',
  },
};
